import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Badge, Button, Divider, Link, List, Popover, Stack, Typography } from '@mui/material';

import { IconWrapper } from 'components/Icon';
import { Loader } from 'components/Loader';
import { paths } from 'routes';

import { useNotifications } from '../api/getNotifications';
import { useUnreadNotificationsCount } from '../api/getUnreadCount';
import { useMarkAllNotificationsRead } from '../api/markRead';
import { NotificationItem } from './NotificationItem';

const DROPDOWN_LIMIT = 5;

const NotificationsDropdown = ({ unreadCount, onClose }) => {
  const { data, isLoading, isError } = useNotifications({ limit: DROPDOWN_LIMIT });
  const { mutate: markAllRead, isLoading: isMarkingAllRead } = useMarkAllNotificationsRead();
  const notifications = data?.notifications ?? [];

  return (
    <Stack width={380}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={2} py={1.5}>
        <Typography variant="h6">Notifications</Typography>
        <Button size="small" onClick={() => markAllRead()} disabled={unreadCount === 0 || isMarkingAllRead}>
          Mark all as read
        </Button>
      </Stack>
      <Divider />

      {isLoading && <Loader style={{ margin: '24px auto' }} />}
      {isError && (
        <Typography variant="body2" color="error" px={2} py={3} textAlign="center">
          Failed to load notifications
        </Typography>
      )}
      {!isLoading && !isError && notifications.length === 0 && (
        <Typography variant="body2" color="text.secondary" px={2} py={3} textAlign="center">
          You have no notifications
        </Typography>
      )}

      <List disablePadding>
        {notifications.map((notification) => (
          <NotificationItem key={notification.notification_id} notification={notification} onOpen={onClose} />
        ))}
      </List>

      <Divider />
      <Stack alignItems="center" py={1.5}>
        <Link component={RouterLink} to={paths.notifications} onClick={onClose} variant="body2" fontWeight={600}>
          View all
        </Link>
      </Stack>
    </Stack>
  );
};

export const NotificationBell = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { data: unreadCount = 0 } = useUnreadNotificationsCount();

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Button
        variant="contained"
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        aria-haspopup="true"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{
          borderRadius: 1.5,
          bgcolor: 'background.paper',
          color: 'text.secondary',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Badge badgeContent={unreadCount} color="error" max={99}>
          <IconWrapper
            icon="bell"
            sx={({ palette }) => ({
              svg: {
                color: palette.background.paper,
                stroke: palette.primary.main,
                strokeWidth: 2,
              },
            })}
          />
        </Badge>
        <Typography variant="button" sx={{ fontWeight: 700, fontSize: '0.625rem', lineHeight: 1.5 }}>
          Notifications
        </Typography>
      </Button>

      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {!!anchorEl && <NotificationsDropdown unreadCount={unreadCount} onClose={handleClose} />}
      </Popover>
    </>
  );
};
